/**
 * Criteria Matching Utilities
 * Mirrors backend MatchingCriteriaFilterBuilder.php logic for consistent FE/BE behavior.
 */

// Mapping from search criteria name_id to caregiver criteria name_id(s)
export const SEARCH_TO_CAREGIVER_CRITERIA_MAP = {
    gender: [],
    acceptable_accent: ['caregiver_accent'],
    lifting_transferring: ['lifting_transferring_moderate_assist', 'lifting_transferring_total_assist'],
    foreign_language: ['foreign_language_basic', 'foreign_language_fluent'],
    personal_care: ['will_do_personal_care'],
}

// 'gte' = caregiver >= requested, 'lte' = caregiver <= requested
export const ORDINAL_ATTRIBUTES = {
    weight: 'gte',
    alzheimers_disease_severity: 'gte',
    dementia_severity: 'gte',
    education: 'gte',
    travel_duration: 'gte',
    cooking_type: 'gte',
    cat_care_type: 'gte',
    dog_care_type: 'gte',
    personal_care_type: 'gte',
    accent: 'lte',
}

/**
 * Restrictive attributes where NO selection means "unrestricted" (matches any search value).
 * If caregiver has values selected, at least one must overlap with the search values.
 * Example: driving_restrictions - caregiver with no restrictions matches all searches.
 */
export const RESTRICTIVE_ATTRIBUTES = ['driving_restrictions']

/**
 * Match-all choices where selecting these specific choices means "skip filtering entirely".
 * Unlike NON_RESTRICTIVE_CHOICES, these don't exclude caregivers when not in search.
 *
 * Example:
 * - travel_abroad: 'can_travel_abroad' requires exact match, anything else skips filtering
 */
export const EXACT_MATCH_ONLY_CHOICES = {
    travel_abroad: ['can_travel_abroad'],
}

/**
 * Non-restrictive choices where selecting these specific choices means "match all".
 * When the search includes these choice IDs, no filtering is applied for that attribute.
 * Additionally, when a search does NOT include ANY choice for these attributes,
 * caregivers WITH selections for that attribute are excluded.
 *
 * Examples:
 * - driving_insurance: 'will_add_to_insurance' matches all; not selecting it excludes those who require it
 * - hypoallergenic: 'hypoallergenic' (optional) matches all; not selecting it excludes those who require hypoallergenic
 */
export const NON_RESTRICTIVE_CHOICES = {
    driving_insurance: ['will_add_to_insurance'],
    hypoallergenic: ['hypoallergenic'],
}

let ordinalChoiceOrdersCache = null

export function buildOrdinalChoiceOrders(criteriaNameDict) {
    const orders = {}
    for (const attrNameId of Object.keys(ORDINAL_ATTRIBUTES)) {
        for (const criteria of Object.values(criteriaNameDict)) {
            const attrDef = criteria.attribute_dict?.[attrNameId]
            if (attrDef?.choices?.length > 0) {
                orders[attrNameId] = attrDef.choices.map((c) => c.id)
                break
            }
        }
    }
    return orders
}

export function initializeOrdinalChoiceOrders(criteriaNameDict) {
    ordinalChoiceOrdersCache = buildOrdinalChoiceOrders(criteriaNameDict)
}

export function getOrdinalChoiceOrders() {
    return ordinalChoiceOrdersCache || {}
}

export function getOrdinalIndex(attrNameId, choiceValue) {
    const orders = getOrdinalChoiceOrders()
    const choices = orders[attrNameId]
    if (!choices) return -1
    const index = choices.indexOf(choiceValue)
    return index >= 0 ? index : -1
}

export function ordinalMeetsRequirement(attrNameId, caregiverValue, requestedValue) {
    const direction = ORDINAL_ATTRIBUTES[attrNameId]
    if (!direction) return caregiverValue === requestedValue

    const caregiverIndex = getOrdinalIndex(attrNameId, caregiverValue)
    const requestedIndex = getOrdinalIndex(attrNameId, requestedValue)

    if (caregiverIndex < 0 || requestedIndex < 0) return false

    return direction === 'gte' ? caregiverIndex >= requestedIndex : caregiverIndex <= requestedIndex
}

/**
 * Check if an attribute is a restrictive attribute.
 */
export function isRestrictiveAttribute(attrNameId) {
    return RESTRICTIVE_ATTRIBUTES.includes(attrNameId)
}

/**
 * Check if the search attribute has a non-restrictive choice selected.
 * Non-restrictive choices mean "match all" - no filtering should be applied.
 */
export function isNonRestrictiveChoice(searchAttr) {
    const nonRestrictiveChoices = NON_RESTRICTIVE_CHOICES[searchAttr.name_id]
    if (!nonRestrictiveChoices) return false

    const selectedChoices = searchAttr.selected_choice_ids || []
    return selectedChoices.some((choiceId) => nonRestrictiveChoices.includes(choiceId))
}

export function shouldSkipExactMatchFiltering(searchAttr) {
    const exactMatchChoices = EXACT_MATCH_ONLY_CHOICES[searchAttr.name_id]
    if (!exactMatchChoices) return false

    const selectedChoices = searchAttr.selected_choice_ids || []
    return !selectedChoices.some((choiceId) => exactMatchChoices.includes(choiceId))
}

/**
 * Handle restrictive attribute matching.
 * If search has EMPTY selections (no restrictions), exclude caregivers WITH restrictions.
 * Caregivers with NO selection for the attribute match any search value.
 * If caregiver HAS selections, at least one must overlap with search values.
 */
export function restrictiveAttributeMatches(searchAttr, caregiverAttr) {
    const searchChoices = searchAttr.selected_choice_ids || []
    const caregiverChoices = caregiverAttr?.selected_choice_ids || []

    if (searchChoices.length === 0) {
        return caregiverChoices.length === 0
    }

    if (caregiverChoices.length === 0) return true

    return caregiverChoices.some((choice) => searchChoices.includes(choice))
}

export function attributeMatches(searchAttr, caregiverAttr) {
    if (searchAttr.name_id !== caregiverAttr.name_id) return false

    const searchChoices = searchAttr.selected_choice_ids || []
    const caregiverChoices = caregiverAttr.selected_choice_ids || []

    if (searchChoices.length === 0) return true

    if (isRestrictiveAttribute(searchAttr.name_id)) {
        return restrictiveAttributeMatches(searchAttr, caregiverAttr)
    }

    if (isNonRestrictiveChoice(searchAttr)) {
        return true
    }

    if (shouldSkipExactMatchFiltering(searchAttr)) {
        return true
    }

    const isOrdinal = ORDINAL_ATTRIBUTES[searchAttr.name_id] !== undefined

    if (isOrdinal) {
        return ordinalMeetsRequirement(searchAttr.name_id, caregiverChoices[0], searchChoices[0])
    } else {
        return searchChoices.every((searchChoice) => caregiverChoices.includes(searchChoice))
    }
}

export function allAttributesMatch(searchAttributes, caregiverAttributes) {
    if (!searchAttributes || searchAttributes.length === 0) return true

    const effectiveSearchAttrs = searchAttributes.filter((attr) => attr.selected_choice_ids && attr.selected_choice_ids.length > 0)

    const allSearchAttrNameIds = searchAttributes.map((attr) => attr.name_id)

    if (!caregiverAttributes || caregiverAttributes.length === 0) {
        if (effectiveSearchAttrs.length === 0) {
            for (const attrNameId of RESTRICTIVE_ATTRIBUTES) {
                if (allSearchAttrNameIds.includes(attrNameId)) {
                    return true
                }
            }
            return true
        }
        const hasOnlyPassThroughAttrs = effectiveSearchAttrs.every(
            (attr) => isRestrictiveAttribute(attr.name_id) || isNonRestrictiveChoice(attr) || shouldSkipExactMatchFiltering(attr)
        )
        return hasOnlyPassThroughAttrs
    }

    for (const attrNameId of RESTRICTIVE_ATTRIBUTES) {
        if (allSearchAttrNameIds.includes(attrNameId)) {
            const searchAttr = searchAttributes.find((sa) => sa.name_id === attrNameId)
            const searchChoices = searchAttr?.selected_choice_ids || []
            if (searchChoices.length === 0) {
                const caregiverAttr = caregiverAttributes.find((ca) => ca.name_id === attrNameId)
                if (caregiverAttr && caregiverAttr.selected_choice_ids && caregiverAttr.selected_choice_ids.length > 0) {
                    return false
                }
            }
        }
    }

    // Handle non-restrictive attribute exclusion
    const searchAttrNameIds = effectiveSearchAttrs.map((attr) => attr.name_id)
    for (const attrNameId of Object.keys(NON_RESTRICTIVE_CHOICES)) {
        if (!searchAttrNameIds.includes(attrNameId)) {
            const caregiverAttr = caregiverAttributes.find((ca) => ca.name_id === attrNameId)
            if (caregiverAttr && caregiverAttr.selected_choice_ids && caregiverAttr.selected_choice_ids.length > 0) {
                return false
            }
        }
    }

    if (effectiveSearchAttrs.length === 0) return true

    return effectiveSearchAttrs.every((searchAttr) => {
        if (isRestrictiveAttribute(searchAttr.name_id)) {
            const caregiverAttr = caregiverAttributes.find((ca) => ca.name_id === searchAttr.name_id)
            return restrictiveAttributeMatches(searchAttr, caregiverAttr)
        }

        if (isNonRestrictiveChoice(searchAttr)) {
            return true
        }

        if (shouldSkipExactMatchFiltering(searchAttr)) {
            return true
        }

        const caregiverAttr = caregiverAttributes.find((ca) => ca.name_id === searchAttr.name_id)
        if (!caregiverAttr) return false

        return attributeMatches(searchAttr, caregiverAttr)
    })
}

export function getCaregiverCriteriaNameIds(searchNameId) {
    return SEARCH_TO_CAREGIVER_CRITERIA_MAP[searchNameId] || [searchNameId]
}

export function getWeightOrdinalIndex(weightValue) {
    return getOrdinalIndex('weight', weightValue)
}

export function getMatchingLiftingCriteria(searchCriteria, caregiverCriterias) {
    const assistLevelAttr = searchCriteria.attributes?.find((a) => a.name_id === 'assist_level')
    const weightAttr = searchCriteria.attributes?.find((a) => a.name_id === 'weight')
    const requestedAssistLevel = assistLevelAttr?.selected_choice_ids?.[0]
    const requestedWeight = weightAttr?.selected_choice_ids?.[0]
    const requestedWeightIndex = getWeightOrdinalIndex(requestedWeight)

    const moderateCriteria = caregiverCriterias.find((c) => c.name_id === 'lifting_transferring_moderate_assist')
    const totalCriteria = caregiverCriterias.find((c) => c.name_id === 'lifting_transferring_total_assist')

    function getCaregiverWeight(criteria) {
        const weightAttr = criteria?.attributes?.find((a) => a.name_id === 'weight')
        return weightAttr?.selected_choice_ids?.[0]
    }

    function weightMeetsRequirement(caregiverWeight) {
        if (requestedWeightIndex < 0) return true
        const caregiverWeightIndex = getWeightOrdinalIndex(caregiverWeight)
        return caregiverWeightIndex >= 0 && caregiverWeightIndex >= requestedWeightIndex
    }

    if (requestedAssistLevel === 'total_assist') {
        if (totalCriteria && weightMeetsRequirement(getCaregiverWeight(totalCriteria))) {
            return totalCriteria
        }
        return null
    }

    if (totalCriteria && weightMeetsRequirement(getCaregiverWeight(totalCriteria))) {
        return totalCriteria
    }
    if (moderateCriteria && weightMeetsRequirement(getCaregiverWeight(moderateCriteria))) {
        return moderateCriteria
    }
    return null
}

export function getMatchingForeignLanguageCriteria(searchCriteria, caregiverCriterias) {
    const fluencyAttr = searchCriteria.attributes?.find((a) => a.name_id === 'foreign_language_fluency')
    const languageAttr = searchCriteria.attributes?.find((a) => a.name_id === 'foreign_language')
    const requestedFluency = fluencyAttr?.selected_choice_ids?.[0]
    const requestedLanguages = languageAttr?.selected_choice_ids || []

    if (requestedLanguages.length === 0) return null

    const fluentCriteria = caregiverCriterias.find((c) => c.name_id === 'foreign_language_fluent')
    const basicCriteria = caregiverCriterias.find((c) => c.name_id === 'foreign_language_basic')

    function hasMatchingLanguage(criteria) {
        if (!criteria) return false
        const langAttr = criteria.attributes?.find((a) => a.name_id === 'foreign_language')
        const caregiverLanguages = langAttr?.selected_choice_ids || []
        return requestedLanguages.every((lang) => caregiverLanguages.includes(lang))
    }

    if (requestedFluency === 'fluent') {
        if (hasMatchingLanguage(fluentCriteria)) {
            return fluentCriteria
        }
        return null
    }

    if (hasMatchingLanguage(fluentCriteria)) {
        return fluentCriteria
    }
    if (hasMatchingLanguage(basicCriteria)) {
        return basicCriteria
    }
    return null
}

/**
 * Check if caregiver certifications match search criteria for professional_certifications.
 * All requested certification types must be present in caregiver's certifications.
 * If state or status filters are specified, they must match as well.
 *
 * @param {object} searchCriteria - The search criteria with attributes for certification, state, status
 * @param {array} caregiverCertifications - Array of caregiver certifications with name, state, status
 * @returns {boolean} True if all requested certifications match
 */
export function certificationsMatchSearch(searchCriteria, caregiverCertifications) {
    if (!caregiverCertifications || caregiverCertifications.length === 0) return false

    const certAttr = searchCriteria.attributes?.find((a) => a.name_id === 'certification')
    const stateAttr = searchCriteria.attributes?.find((a) => a.name_id === 'state')
    const statusAttr = searchCriteria.attributes?.find((a) => a.name_id === 'status')

    const requestedCertTypes = certAttr?.selected_choice_ids || []
    const requestedStates = stateAttr?.selected_choice_ids || []
    const requestedStatuses = statusAttr?.selected_choice_ids || []

    if (requestedCertTypes.length === 0) return false

    return requestedCertTypes.every((requestedType) => {
        const normalizedRequestedType = requestedType.toUpperCase()

        return caregiverCertifications.some((cert) => {
            const certName = (cert.name || '').toUpperCase()
            if (certName !== normalizedRequestedType) return false

            if (requestedStates.length > 0) {
                const certState = (cert.state || '').toUpperCase()
                const matchesState = requestedStates.some((s) => s.toUpperCase() === certState)
                if (!matchesState) return false
            }

            if (requestedStatuses.length > 0) {
                const certStatus = (cert.status || '').toLowerCase()
                const matchesStatus = requestedStatuses.some((s) => s.toLowerCase() === certStatus)
                if (!matchesStatus) return false
            }

            return true
        })
    })
}

/**
 * Get matching certifications from caregiver that match the search criteria.
 * Returns an object formatted like a criteria for consistent display.
 *
 * @param {object} searchCriteria - The search criteria with attributes for certification, state, status
 * @param {array} caregiverCertifications - Array of caregiver certifications with name, state, status
 * @returns {object|null} Matching criteria object or null if no match
 */
export function getMatchingCertifications(searchCriteria, caregiverCertifications) {
    if (!certificationsMatchSearch(searchCriteria, caregiverCertifications)) {
        return null
    }

    const certAttr = searchCriteria.attributes?.find((a) => a.name_id === 'certification')
    const stateAttr = searchCriteria.attributes?.find((a) => a.name_id === 'state')
    const statusAttr = searchCriteria.attributes?.find((a) => a.name_id === 'status')

    const requestedCertTypes = certAttr?.selected_choice_ids || []
    const requestedStates = stateAttr?.selected_choice_ids || []
    const requestedStatuses = statusAttr?.selected_choice_ids || []

    const matchingCerts = caregiverCertifications.filter((cert) => {
        const certName = (cert.name || '').toUpperCase()
        const matchesType = requestedCertTypes.some((t) => t.toUpperCase() === certName)
        if (!matchesType) return false

        if (requestedStates.length > 0) {
            const certState = (cert.state || '').toUpperCase()
            const matchesState = requestedStates.some((s) => s.toUpperCase() === certState)
            if (!matchesState) return false
        }

        if (requestedStatuses.length > 0) {
            const certStatus = (cert.status || '').toLowerCase()
            const matchesStatus = requestedStatuses.some((s) => s.toLowerCase() === certStatus)
            if (!matchesStatus) return false
        }

        return true
    })

    const types = matchingCerts.map((c) => c.name.toUpperCase()).join(', ')
    const states = [...new Set(matchingCerts.map((c) => c.state))].join(', ')
    const statuses = [...new Set(matchingCerts.map((c) => c.status))].join(', ')
    const displayText = `${types} Certified in ${states} (${statuses})`

    return {
        name_id: 'professional_certifications',
        certifications: matchingCerts,
        displayText,
        attributes: [
            {
                name_id: 'certification',
                selected_choice_ids: matchingCerts.map((c) => c.name.toLowerCase()),
            },
            {
                name_id: 'state',
                selected_choice_ids: [...new Set(matchingCerts.map((c) => c.state))],
            },
            {
                name_id: 'status',
                selected_choice_ids: [...new Set(matchingCerts.map((c) => c.status.toLowerCase()))],
            },
        ],
    }
}

export function criteriaMatchesCaregiver(searchCriteria, caregiver, caregiverCriterias) {
    if (searchCriteria.name_id === 'gender') {
        const requestedGender = searchCriteria.attributes?.[0]?.selected_choice_ids?.[0]
        return requestedGender && caregiver.gender === requestedGender
    }

    if (searchCriteria.name_id === 'professional_certifications') {
        return certificationsMatchSearch(searchCriteria, caregiver.certifications)
    }

    if (searchCriteria.name_id === 'lifting_transferring') {
        return getMatchingLiftingCriteria(searchCriteria, caregiverCriterias) !== null
    }

    if (searchCriteria.name_id === 'foreign_language') {
        return getMatchingForeignLanguageCriteria(searchCriteria, caregiverCriterias) !== null
    }

    const caregiverNameIds = getCaregiverCriteriaNameIds(searchCriteria.name_id)

    return caregiverCriterias.some((criteria) => {
        if (!caregiverNameIds.includes(criteria.name_id)) return false
        return allAttributesMatch(searchCriteria.attributes || [], criteria.attributes || [])
    })
}

export function getMatchingCaregiverCriteria(searchCriteria, caregiver, caregiverCriterias) {
    if (searchCriteria.name_id === 'gender') {
        const requestedGender = searchCriteria.attributes?.[0]?.selected_choice_ids?.[0]
        if (requestedGender && caregiver.gender === requestedGender) {
            return { name_id: 'gender', attributes: [{ name_id: 'gender', selected_choice_ids: [caregiver.gender] }] }
        }
        return null
    }

    if (searchCriteria.name_id === 'professional_certifications') {
        return getMatchingCertifications(searchCriteria, caregiver.certifications)
    }

    if (searchCriteria.name_id === 'lifting_transferring') {
        return getMatchingLiftingCriteria(searchCriteria, caregiverCriterias)
    }

    if (searchCriteria.name_id === 'foreign_language') {
        return getMatchingForeignLanguageCriteria(searchCriteria, caregiverCriterias)
    }

    const caregiverNameIds = getCaregiverCriteriaNameIds(searchCriteria.name_id)
    const searchAttributes = searchCriteria.attributes || []

    return (
        caregiverCriterias.find((criteria) => {
            if (!caregiverNameIds.includes(criteria.name_id)) return false
            return allAttributesMatch(searchAttributes, criteria.attributes || [])
        }) || null
    )
}
